/**
 * ExplainableRecommendationLayer.ts — the Explainable Recommendation Layer™.
 *
 * REQUIRED stage: every recommendation the Recommendation Engine produces MUST
 * pass through here before it reaches the Roamer Local UI. It enriches each
 * ranked recommendation with a transparent `explanation` object (match score,
 * confidence, why-this, why-not, supporting metrics, and how the alternatives
 * compare) so the UI can render reasoning without any further processing.
 *
 * Pure & deterministic: no React, no storage, no network. Every string it
 * emits is DERIVED from the recommendation's own attributes and the already-
 * computed match score — nothing is hardcoded per city or per venue.
 */

import type { RecommendationAttributes, ReasonPolarity, Recommendation } from '../types';
import type {
  AlternativeComparison,
  ConfidenceLabel,
  ExplainedRecommendation,
  RecommendationExplanation,
  SupportingMetric,
} from './types';

const MAX_ALTERNATIVES = 3;
const MAX_DRAWBACKS = 3;
const MAX_ADVANTAGES = 2;
const MAX_WHY_THIS = 6;
const MAX_WHY_NOT = 3;

// ─── Metric formatting ─────────────────────────────────────────────────────────
// One place that turns a raw attribute into a labelled, display-ready value.

type NumericAttr = {
  [K in keyof RecommendationAttributes]-?: NonNullable<RecommendationAttributes[K]> extends number ? K : never;
}[keyof RecommendationAttributes];

interface MetricFormat {
  label: string;
  format: (v: number) => string;
}

const METRIC_FORMATS: Record<NumericAttr, MetricFormat> = {
  internetMbps: { label: 'Internet', format: (v) => `${Math.round(v)} Mbps` },
  monthlyCost: { label: 'Monthly Cost', format: (v) => `$${Math.round(v).toLocaleString()}/mo` },
  priceLevel: { label: 'Price', format: (v) => '$'.repeat(Math.max(1, Math.min(4, Math.round(v)))) },
  rating: { label: 'Rating', format: (v) => `${v.toFixed(1)} ★` },
  reviewCount: { label: 'Reviews', format: (v) => `${Math.round(v).toLocaleString()} reviews` },
  quietness: { label: 'Quietness', format: (v) => `${Math.round(v)}/100 quiet` },
  walkabilityMeters: { label: 'Walkability', format: (v) => `${Math.round(v)} m walk` },
  distanceKm: { label: 'Distance', format: (v) => `${v.toFixed(1)} km away` },
  safety: { label: 'Safety', format: (v) => `${Math.round(v)}/100 safety` },
  nearCoworkingMeters: { label: 'Near Coworking', format: (v) => `${Math.round(v)} m to coworking` },
  transitScore: { label: 'Transit', format: (v) => `${Math.round(v)}/100 transit` },
  englishFriendly: { label: 'English', format: (v) => `${Math.round(v)}/100 English` },
  nightlife: { label: 'Nightlife', format: (v) => `${Math.round(v)}/100 nightlife` },
  natureAccess: { label: 'Nature', format: (v) => `${Math.round(v)}/100 nature` },
  beachAccessKm: { label: 'Beach', format: (v) => `${v.toFixed(1)} km to beach` },
};

/** Boolean amenities that read as a plain positive when present. */
const BOOLEAN_METRICS: { key: keyof RecommendationAttributes; label: string }[] = [
  { key: 'hasAirConditioning', label: 'Air Conditioning' },
  { key: 'hasPowerOutlets', label: 'Power Outlets' },
  { key: 'petFriendly', label: 'Pet Friendly' },
  { key: 'open24h', label: 'Open 24h' },
  { key: 'longStayFriendly', label: 'Long-stay Friendly' },
];

// ─── Comparison metrics ────────────────────────────────────────────────────────
// Drives "how does alternative X compare to the primary?". Direction + phrasing
// per metric; a difference must clear the threshold to be worth surfacing.

interface ComparisonMetric {
  key: NumericAttr;
  /** Which direction is BETTER for a Roamer. */
  better: 'higher' | 'lower';
  /** Phrase when the alternative is worse than the primary. */
  worse: string;
  /** Phrase when the alternative is better than the primary. */
  advantage: string;
  /** Minimum relative gap (fraction of the larger value) to count. */
  minRel?: number;
  /** Minimum absolute gap to count (for bounded 0–100 / small scales). */
  minAbs?: number;
}

const COMPARISON_METRICS: ComparisonMetric[] = [
  { key: 'internetMbps', better: 'higher', worse: 'Internet is slower', advantage: 'Faster internet', minRel: 0.1 },
  { key: 'monthlyCost', better: 'lower', worse: 'Higher monthly cost', advantage: 'Lower monthly cost', minRel: 0.08 },
  { key: 'priceLevel', better: 'lower', worse: 'More expensive', advantage: 'More affordable', minAbs: 1 },
  { key: 'rating', better: 'higher', worse: 'Lower rating', advantage: 'Higher rating', minAbs: 0.3 },
  { key: 'safety', better: 'higher', worse: 'Lower safety score', advantage: 'Safer area', minAbs: 8 },
  { key: 'quietness', better: 'higher', worse: 'Noisier surroundings', advantage: 'Quieter surroundings', minAbs: 12 },
  { key: 'walkabilityMeters', better: 'lower', worse: 'Less walkable', advantage: 'More walkable', minRel: 0.2 },
  { key: 'distanceKm', better: 'lower', worse: 'Farther out', advantage: 'More central', minRel: 0.2 },
  { key: 'nearCoworkingMeters', better: 'lower', worse: 'Farther from coworking', advantage: 'Closer to coworking', minRel: 0.2 },
  { key: 'transitScore', better: 'higher', worse: 'Farther from transit', advantage: 'Better transit access', minAbs: 8 },
  { key: 'englishFriendly', better: 'higher', worse: 'Less English-friendly', advantage: 'More English-friendly', minAbs: 10 },
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

function starsFromScore(score: number): number {
  return Math.round((score / 20) * 2) / 2; // 0–5, half-star increments
}

function confidenceLabel(confidence: number): ConfidenceLabel {
  if (confidence >= 70) return 'High';
  if (confidence >= 40) return 'Medium';
  return 'Low';
}

function fitWord(score: number): string {
  if (score >= 90) return 'Excellent fit';
  if (score >= 78) return 'Strong fit';
  if (score >= 62) return 'Good fit';
  if (score >= 48) return 'Fair fit';
  return 'Weak fit';
}

/** Formatted, polarity-tagged metrics that back a recommendation. */
function buildSupportingMetrics(rec: Recommendation): SupportingMetric[] {
  const reasonLabels = new Set(rec.match?.reasons.map((r) => r.label) ?? []);
  const concernLabels = new Set(rec.match?.concerns.map((r) => r.label) ?? []);
  const metrics: SupportingMetric[] = [];

  for (const key of Object.keys(METRIC_FORMATS) as NumericAttr[]) {
    const raw = rec.attributes[key];
    if (typeof raw !== 'number') continue;
    const fmt = METRIC_FORMATS[key];
    // Tie polarity to the reasons the scorer already surfaced, so the metric
    // colouring never contradicts the score.
    let polarity: ReasonPolarity = 'neutral';
    if ([...concernLabels].some((l) => labelMatchesMetric(l, fmt.label))) polarity = 'concern';
    else if ([...reasonLabels].some((l) => labelMatchesMetric(l, fmt.label))) polarity = 'positive';
    metrics.push({ label: fmt.label, value: fmt.format(raw), polarity });
  }

  for (const b of BOOLEAN_METRICS) {
    if (rec.attributes[b.key] === true) {
      metrics.push({ label: b.label, value: 'Yes', polarity: 'positive' });
    }
  }

  return metrics;
}

/** Loose association between a scorer reason label and a metric label. */
function labelMatchesMetric(reasonLabel: string, metricLabel: string): boolean {
  const r = reasonLabel.toLowerCase();
  const m = metricLabel.toLowerCase();
  return r.includes(m) || m.includes(r.split(' ')[0]);
}

/** Compare one alternative against the primary and describe the differences. */
function compareAlternative(primary: Recommendation, alt: Recommendation): AlternativeComparison {
  const drawbacks: string[] = [];
  const advantages: string[] = [];

  for (const cm of COMPARISON_METRICS) {
    const pv = primary.attributes[cm.key];
    const av = alt.attributes[cm.key];
    if (typeof pv !== 'number' || typeof av !== 'number' || pv === av) continue;

    const diff = Math.abs(av - pv);
    const rel = diff / Math.max(Math.abs(pv), Math.abs(av), 1);
    const passes = (cm.minRel != null && rel >= cm.minRel) || (cm.minAbs != null && diff >= cm.minAbs);
    if (!passes) continue;

    const altIsBetter = cm.better === 'higher' ? av > pv : av < pv;
    if (altIsBetter) advantages.push(cm.advantage);
    else drawbacks.push(cm.worse);
  }

  return {
    id: alt.id,
    title: alt.title,
    matchScore: alt.match?.score ?? 0,
    drawbacks: drawbacks.slice(0, MAX_DRAWBACKS),
    advantages: advantages.slice(0, MAX_ADVANTAGES),
  };
}

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Enrich a SINGLE recommendation with its explanation, given the full ranked
 * set so alternatives can be compared. `rec` must already carry a `match`
 * (attached by the engine after ranking).
 */
export function explainRecommendation(
  rec: Recommendation,
  rankedSet: Recommendation[],
): ExplainedRecommendation {
  const match = rec.match ?? { score: 50, reasons: [], concerns: [], confidence: 0 };

  const alternatives = rankedSet
    .filter((r) => r.id !== rec.id)
    .slice(0, MAX_ALTERNATIVES)
    .map((alt) => compareAlternative(rec, alt));

  const explanation: RecommendationExplanation = {
    matchScore: match.score,
    matchStars: starsFromScore(match.score),
    confidence: match.confidence,
    confidenceLabel: confidenceLabel(match.confidence),
    headline: `${Math.round(match.score)} Match · ${fitWord(match.score)}`,
    whyThis: match.reasons.slice(0, MAX_WHY_THIS).map((r) => r.label),
    whyNot: match.concerns.slice(0, MAX_WHY_NOT).map((r) => r.label),
    supportingMetrics: buildSupportingMetrics(rec),
    alternatives,
  };

  return { ...rec, explanation };
}

/**
 * The REQUIRED layer entry point: enrich an entire ranked recommendation set.
 * The order of `ranked` is preserved (it is the ranking the engine produced).
 */
export function explainRecommendations(ranked: Recommendation[]): ExplainedRecommendation[] {
  return ranked.map((rec) => explainRecommendation(rec, ranked));
}
